import { useEffect, useMemo } from 'react';
import { Preview } from 'react-dnd-multi-backend';
import { useSelector } from 'react-redux';
import { Button } from '@mui/material';

import { PlaybookTabs, SortDirection } from '@labradorsports/constants';
import { getCurrentFolderItems, useSite, getSelectedCount } from '@labradorsports/utils';

import { PlayType, PlaybookBrowseViewMode, SortOptions } from '../../shared/constants.js';
import { useDispatcher } from '../../shared/hooks/index.js';
import { DnDContext } from '../../shared/structural/index.js';
import {
    Selectors,
    useHasPersonalQuery,
    useLoadViewedPlaysQuery,
    useMoveToFolderMutation,
    useMyPlaybookQuery,
    useTeamPlaybookQuery,
    useDropPlaybookItemMutation,
    RouterSelectors,
    routerActions,
} from '../../store/index.js';
import { PlayListItem } from '../index.js';
import useStartSharing from '../sharing/useStartSharing.js';

interface Props {
    onSelectionChange?: (selected: string[], selectedCount: number) => void;
    onItemClick?: (item: any) => void;
    deleteItem?: (itemId: string) => void;
    unshareItem?: (itemId: string) => void;
    moveToFolderItem?: (itemId: string) => void;
    copyPlay?: (play: any) => void;
    readOnly?: boolean;
    playbookType: string;
    selectedItemIds?: string[];
    dropdowns?: boolean;
    dragAndDrop?: boolean;
}

const PlaylistItemDragPreview = ({ item, itemType, style }: any) => {
    // TODO: why is this preview getting items from other Dnd contexts?
    if (itemType !== PlayType) {
        return null;
    }

    return (
        <div
            style={{
                ...style,
                zIndex: 100,
                width: '350px',
                marginTop: '-60px',
            }}
        >
            {item.selectedItems ? (
                <div className="play-list-item list-item p-3">{item.selectedItems.length} items selected</div>
            ) : (
                <PlayListItem item={item} playlist={[]} viewed hideDropdown readOnly />
            )}
        </div>
    );
};

const PlayList: FC<Props> = ({
    readOnly,
    onItemClick,
    playbookType,
    onSelectionChange,
    selectedItemIds = [],
    deleteItem,
    unshareItem,
    moveToFolderItem,
    copyPlay,
    dropdowns,
    dragAndDrop,
}) => {
    const openFolder = useDispatcher(routerActions.OpenPlayFolder);
    const setPlaybookFilter = useDispatcher(routerActions.SetPlaybookFilter);
    const playbookFilter = useSelector(RouterSelectors.playbookFilter);
    const [moveToFolder] = useMoveToFolderMutation({ playbookType });
    const [dropPlaybookItem] = useDropPlaybookItemMutation();
    const startShare = useStartSharing();

    const isTeamPlaybook = playbookType === PlaybookTabs.TEAM;
    const folderId = useSelector(RouterSelectors.folderId);
    const { data: hasPersonal } = useHasPersonalQuery();
    const activeTeam = useSelector(Selectors.activeTeam);
    const { data: myPlaybook } = useMyPlaybookQuery();
    const { data: teamPlaybook } = useTeamPlaybookQuery();

    const { data: viewed } = useLoadViewedPlaysQuery({ activeTeam, folderId }, { skip: !isTeamPlaybook });

    const { Config } = useSite();

    // Derive filter values from router state
    const activeTags = playbookFilter?.tags ?? [];
    const sortField = playbookFilter?.sortBy ?? SortOptions.DEFAULT;
    const sortDirection = playbookFilter?.sortDirection ?? SortDirection.ASC;
    const unviewedOnly = playbookFilter?.unviewedOnly ?? false; // Only used for team playbooks

    const [playlist = [], folders = []] = (() => {
        if (isTeamPlaybook) {
            return teamPlaybook ? [teamPlaybook.shared, teamPlaybook.folders] : [];
        }

        return myPlaybook ? [myPlaybook.plays, myPlaybook.folders] : [];
    })();

    const hiddenPlays = !isTeamPlaybook && !hasPersonal ? playlist.slice(10) : [];
    const items = getCurrentFolderItems(playlist, folders, folderId);

    const setSelectedItemIds = (items: string[]) => {
        const selCount = getSelectedCount(Config, items, folders, playlist);

        if (onSelectionChange) {
            onSelectionChange(items, selCount);
        }
    };

    const deselectAll = () => {
        setSelectedItemIds([]);
    };

    useEffect(() => {
        setSelectedItemIds([]);
    }, [folderId, playbookType, activeTeam]);

    const included = [];

    const filtered = items
        .filter((play: any) => {
            const filterTags = activeTags.length > 0;
            const tagsMatch = activeTags.some((tag) => play.tags?.includes(tag));
            const filterViewed = isTeamPlaybook && unviewedOnly;
            const isUnviewed = viewed && !viewed.includes(play.id);

            return (!filterTags || tagsMatch) && (!filterViewed || isUnviewed);
        })
        // Only include a given item id once
        .filter((play: any) => {
            if (!included.includes(play.id)) {
                included.push(play.id);
                return true;
            }

            return false;
        })
        .map((item: any) => ({
            ...item,
            hidden: hiddenPlays.some((pl: any) => pl.id === item.id),
        }));

    const selectItem = (itemId: any) => {
        const newSelected = selectedItemIds.includes(itemId)
            ? selectedItemIds.filter((id) => id !== itemId)
            : [...selectedItemIds, itemId];

        setSelectedItemIds(newSelected);
    };

    const itemClick = (item) => {
        if (item.sport) {
            if (typeof onItemClick === 'function') {
                onItemClick(item.id);
            } else {
                selectItem(item.id);
            }
        } else {
            openFolder(item.id);
        }
    };

    const itemDrop = (target) => async (dropped, position) => {
        const itemIds = dropped.selectedItems ?? [dropped.id];
        if (position === 'middle') {
            await moveToFolder({
                itemIds,
                targetFolderId: target.id,
            });

            if (dropped.selectedItems) {
                deselectAll();
            }
        } else {
            const currentItemPosition = items.findIndex((item) => item.id === target.id);

            await dropPlaybookItem({
                itemIds,
                newPosition: position === 'top' ? currentItemPosition : currentItemPosition + 1,
                playbookType,
            });
        }
    };

    const resetFilters = () => {
        setPlaybookFilter({
            tags: [],
            sortBy: SortOptions.DEFAULT,
            sortDirection: SortDirection.ASC,
            unviewedOnly: false,
            viewMode: playbookFilter?.viewMode ?? PlaybookBrowseViewMode.CARD,
        });
    };

    const sortedItems = useMemo(() => {
        return filtered.slice().sort((_a, _b) => {
            let [a, b] = [_a, _b];

            if (sortDirection === SortDirection.DESC) {
                [a, b] = [b, a];
            }

            if (sortField === SortOptions.NAME) {
                return a.name.localeCompare(b.name);
            }

            if (sortField === SortOptions.CREATED_DATE) {
                if (!a.createdDate || !b.createdDate) {
                    return 0;
                }

                return a.createdDate.getTime() - b.createdDate.getTime();
            }

            return 0;
        });
    }, [filtered, sortField, sortDirection]);

    const isFiltered = activeTags.length > 0 || sortField !== SortOptions.DEFAULT || unviewedOnly;

    return (
        <DnDContext>
            <Preview generator={PlaylistItemDragPreview} />
            <div className={`play-list ${selectedItemIds.length > 0 ? 'has-selected' : ''}`}>
                {sortedItems.map((item) => (
                    <PlayListItem
                        key={item.id}
                        item={item}
                        playlist={playlist}
                        activeTags={activeTags}
                        deleteClick={() => deleteItem(item.id)}
                        unshareClick={() => unshareItem(item.id)}
                        moveToFolder={() => moveToFolderItem(item.id)}
                        copyPlay={() => copyPlay(item)}
                        readOnly={readOnly}
                        playbookType={playbookType}
                        selectedItems={selectedItemIds}
                        onClick={() => itemClick(item)}
                        onSelect={typeof onSelectionChange === 'function' ? () => selectItem(item.id) : null}
                        itemDrop={dragAndDrop && !isFiltered ? itemDrop(item) : null}
                        hideDropdown={!dropdowns}
                        startShare={startShare}
                        viewed={myPlaybook.plays?.some((play) => play.id === item.id) || viewed?.includes(item.id)}
                    />
                ))}
                {filtered.length === 0 ? (
                    <div className="empty-filter">
                        <p>
                            {/* If there are 0 plays and we have no filter applied and not in a folder, the Playlist component will not be shown at all */}
                            {activeTags.length === 0
                                ? 'There are no plays in this folder yet. Use the ADD TO FOLDER menu option on a play to add one.'
                                : 'There are no plays matching your filter. Try updating your selection above.'}
                        </p>
                        <div className="text-center">
                            <Button onClick={resetFilters}>Reset Filters</Button>
                        </div>
                    </div>
                ) : null}
            </div>
        </DnDContext>
    );
};

export default PlayList;
